import { Box, Card, CardActionArea, Chip, Stack, useMediaQuery } from '@mui/material'
import CampaignImage from '~/components/CampaignImage'
import { CampaignSnsRequirementFragment, CampaignStatus, ImageUrl } from '~/generated/graphql'
import NextLink from 'next/link'
import { FC } from 'react'
import { pagesPath } from '~/lib/$path'
import { decodeId } from '~/lib/convertId'
import { CampaignInfo } from './CampaignInfo'
import { CampaignSnsRequirements } from '~/lib/types/Campaign'
import { theme } from '~/lib/theme'
import { isRevisionRequired, isApproved } from '~/lib/CampaignStatus'

type Props = {
  campaign: {
    id: string
    name: string
    status: CampaignStatus
    coverImageUrls: readonly ImageUrl[]
    gross?: number | null
    nearestStationName?: string | null
    offerStarted?: string | null
    offerEnded?: string | null
    campaignSnsRequirements?: CampaignSnsRequirements[] | readonly CampaignSnsRequirementFragment[] | null
  }
}

type CampaignStatusChipProps = {
  status: CampaignStatus
}

export const ResponsiveWaitingCampaignCell: FC<Props> = ({ campaign }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const coverImageUrls = campaign.coverImageUrls[0]

  const campaignId = decodeId(campaign.id).id
  return (
    <Card elevation={0} sx={{ flex: isMobile ? undefined : '0 0 185px' }}>
      <NextLink href={pagesPath.campaigns._campaignId(campaignId).$url()} passHref legacyBehavior>
        <CardActionArea>
          <Stack gap={isMobile ? 2 : 1} direction={isMobile ? 'row' : 'column'}>
            <Box
              position="relative"
              sx={{
                width: isMobile ? '115px' : '100%',
                minWidth: isMobile ? '115px' : undefined,
                aspectRatio: '1 / 1',
              }}
            >
              {/* キャンペーン画像 */}
              <CampaignImage
                image={{
                  url: coverImageUrls?.original ?? '',
                  w480xh480: coverImageUrls && { url: coverImageUrls.medium },
                  w120xh120: coverImageUrls && { url: coverImageUrls.small },
                }}
                imageSize="small"
                variant="noBorder"
              />
            </Box>
            <Stack gap={1} sx={{ width: '100%' }}>
              <CampaignStatusChip status={campaign.status} />

              {/* キャンペーン情報 */}
              <CampaignInfo
                campaignName={campaign.name}
                campaignNameStyle="ellipsis"
                campaignSnsRequirements={campaign.campaignSnsRequirements}
                gross={campaign.gross}
                nearestStation={campaign.nearestStationName}
                offerStarted={campaign.offerStarted}
                offerEnded={campaign.offerEnded}
              />
            </Stack>
          </Stack>
        </CardActionArea>
      </NextLink>
    </Card>
  )
}

export const CampaignStatusChip: React.FC<CampaignStatusChipProps> = ({ status }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const baseChipStyle = {
    fontSize: '10px',
    fontWeight: 'bold',
    mt: isMobile ? 0 : 1,
  }

  if (isRevisionRequired(status)) {
    return (
      <Chip
        size="small"
        label="修正が必要です"
        sx={{
          ...baseChipStyle,
          backgroundColor: 'error.190p',
          color: 'error.main',
        }}
      />
    )
  }

  if (isApproved(status)) {
    return (
      <Chip
        size="small"
        label="承認済みです"
        sx={{
          ...baseChipStyle,
          backgroundColor: 'success.190p',
          color: 'success.main',
        }}
      />
    )
  }

  return null
}
